import type { AIVideoSpec, AIScene } from '@/remotion/ai-schema';

export const GENERATION_MODES = ['animated-images', 'heygen-avatar'] as const;
export type GenerationMode = (typeof GENERATION_MODES)[number];

/**
 * Modes that were removed but may still appear in stored specs or older client payloads.
 * 'sora-native-lipsync' (Sora 2 native speech) was dropped in 2026-08 together with Sora.
 */
export const LEGACY_GENERATION_MODES = ['sora-native-lipsync'] as const;

export type SubtitleType = 'video' | 'text';
export type SubtitleMode = 'ass-burn' | 'remotion';
export type MusicSelectionMode = 'rotate' | 'locked';
export type MusicProfile = 'brand-safe' | 'uplift' | 'dramatic';

export interface GenerationModePolicy {
  mode: GenerationMode;
  subtitleType: SubtitleType;
  subtitleMode: SubtitleMode;
  captionsEnabled: boolean;
  /** Kept for API-shape compatibility; no current provider generates native speech, so this is always false. */
  nativeAudio: boolean;
  musicSelectionMode: MusicSelectionMode;
  musicProfile: MusicProfile;
}

export function isGenerationMode(value: unknown): value is GenerationMode {
  return typeof value === 'string' && (GENERATION_MODES as readonly string[]).includes(value);
}

export function isLegacyGenerationMode(value: unknown): boolean {
  return typeof value === 'string' && (LEGACY_GENERATION_MODES as readonly string[]).includes(value);
}

/**
 * Zod preprocess helper: turns a removed mode into `undefined` so old specs and payloads still
 * parse and fall through to mode inference instead of failing validation.
 */
export function dropLegacyGenerationMode(value: unknown): unknown {
  return isLegacyGenerationMode(value) ? undefined : value;
}

export function inferGenerationModeFromSpec(spec: AIVideoSpec): GenerationMode {
  if (isGenerationMode(spec.generationMode)) return spec.generationMode;
  const scenes = Array.isArray(spec.scenes) ? spec.scenes : [];

  const hasHeyGen = scenes.some((scene) => scene.videoSource === 'heygen');
  if (hasHeyGen) return 'heygen-avatar';

  return 'animated-images';
}

export function resolveGenerationMode(params: {
  requestedMode?: unknown;
  spec: AIVideoSpec;
}): GenerationMode {
  if (isGenerationMode(params.requestedMode)) return params.requestedMode;
  return inferGenerationModeFromSpec(params.spec);
}

export function buildGenerationModePolicy(mode: GenerationMode): GenerationModePolicy {
  if (mode === 'heygen-avatar') {
    return {
      mode,
      subtitleType: 'video',
      subtitleMode: 'ass-burn',
      captionsEnabled: true,
      nativeAudio: false,
      musicSelectionMode: 'rotate',
      musicProfile: 'brand-safe',
    };
  }

  return {
    mode,
    subtitleType: 'text',
    subtitleMode: 'remotion',
    captionsEnabled: true,
    nativeAudio: false,
    musicSelectionMode: 'rotate',
    musicProfile: 'dramatic',
  };
}

function applyHeyGenDefaults(scene: AIScene): AIScene {
  if (scene.videoSource) return scene;
  if (!scene.narrationText) return scene;
  return {
    ...scene,
    videoSource: 'heygen',
  };
}

export function applyGenerationModeDefaults(params: {
  spec: AIVideoSpec;
  mode: GenerationMode;
}): AIVideoSpec {
  if (params.mode === 'heygen-avatar') {
    return {
      ...params.spec,
      generationMode: params.mode,
      scenes: params.spec.scenes.map((scene) => applyHeyGenDefaults(scene)),
    };
  }

  return {
    ...params.spec,
    generationMode: params.mode,
  };
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
